import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../controls/buttons/FooterModalButtons";
import { ConfirmModal } from "../../../../../controls/modals/ConfirmModal";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { MaterialAutocomplete } from "../../../../../shared/autocompletes/MaterialAutocomplete";
import {
  CATALOG_BIN_INITIAL_VALUES,
  CatalogBin,
} from "../../../models/CatalogBin";
import {
  deleteCatalogBin,
  upsertCatalogBin,
} from "../../../repositories/CatalogBinRepository";
import { validateNoSpecialChars } from "../../validates";
import { useCatalogBinContext } from "../context/CatalogBinContext";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  bin: CatalogBin | null;
  showModal: "new" | "edit" | "delete" | "";
  onHide: ({ entityId, shouldUpdate }: onModalHideProps) => void;
  refreshData: Function;
};
export const BinModal = ({ bin, showModal, onHide, refreshData }: Props) => {
  const { state } = useCatalogBinContext();

  const [notificationMessage, setNotificationMessage] = useState("");
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [existingBin, setExistingBin] = useState("");
  const [title, setTitle] = useState("");

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<CatalogBin>({
    defaultValues: CATALOG_BIN_INITIAL_VALUES,
  });

  const isFormDirty =
    !(Object.keys(dirtyFields).length === 0) || showModal == "new";

  const onSubmit: SubmitHandler<CatalogBin> = async (data: CatalogBin) => {
    setIsSubmitLoading(true);
    const bins: CatalogBin[] = state.bins || [];
    if (showModal === "delete") {
      setIsSubmitLoading(true);
      const resp = await deleteCatalogBin(bin?.BinId ?? null);
      if (resp.ok) {
        await refreshData();
        onHide({ shouldUpdate: true });
        setNotificationMessage("The bin was deleted successfully");
      } else {
        setError(resp.message);
      }
      setIsSubmitLoading(false);
    } else {
      if (
        bins.some(
          (bin) => bin.BinName === data.BinName && bin.BinId !== data.BinId,
        )
      ) {
        setExistingBin(data.BinName);
        setConfirmOpen(true);
      } else {
        await performUpsert(data);
      }
    }
  };

  const performUpsert = async (data: CatalogBin) => {
    const resp = await upsertCatalogBin(data);
    if (resp.ok) {
      const entityId = get(resp, "data.tables[0].rows[0].Id", 0);
      await refreshData();
      setNotificationMessage("The bin was saved successfully");
      onHide({ entityId: entityId, shouldUpdate: true });
    } else {
      setError(resp.message);
      onHide({ shouldUpdate: false });
    }
    setIsSubmitLoading(false);
  };

  useEffect(() => {
    if (showModal != "") {
      setTitle(showModal.toUpperCase());
      reset(bin || CATALOG_BIN_INITIAL_VALUES);
    }
  }, [showModal]);

  return (
    <>
      <ConfirmModal
        show={confirmOpen}
        onHide={(proceed: boolean) => {
          if (proceed) {
            performUpsert(getValues());
            onHide({ shouldUpdate: true });
          }
          setConfirmOpen(false);
          setIsSubmitLoading(false);
        }}
        existingName={existingBin}
        label="Bin"
      />
      <MesfModal
        title={`${title} BIN`}
        open={showModal != ""}
        handleClose={() => onHide({ shouldUpdate: false })}
        id="bin-modal"
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers style={{ padding: "15px 30px" }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={12}>
                <Controller
                  name="BinName"
                  control={control}
                  rules={{
                    required: "Name is required",
                    validate: validateNoSpecialChars,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      label="Name"
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Controller
                  name="MaterialId"
                  control={control}
                  rules={{ required: "Material is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <MaterialAutocomplete
                      label="Material"
                      value={field.value}
                      onChange={field.onChange}
                      fieldError={error}
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Controller
                  name="Enabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="Enabled"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
            <FooterModalButtons
              isSubmitLoading={isSubmitLoading}
              onHide={() => onHide({ shouldUpdate: false })}
              buttonLabel={showModal === "delete" ? "Delete" : "Save"}
              buttonColor={showModal === "delete" ? "secondary" : "primary"}
              disabled={!isFormDirty && showModal !== "delete"}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error with Bin"
      />
    </>
  );
};
